'use client';

import Image from 'next/image';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import LinkIcon from '@mui/icons-material/Link';
import { Box, IconButton, Typography } from '@mui/material';

import Avatar from '@/components/common/Avatar';
import type { EventResponse } from '@/types';

interface MobileMarketHeaderProps {
  event: EventResponse;
  formattedTotalVolume: string;
  isBookmarked: boolean;
  onBookmarkClick: () => void;
  onShareClick: () => void;
}

export function MobileMarketHeader({
  event,
  formattedTotalVolume,
  isBookmarked,
  onBookmarkClick,
  onShareClick,
}: MobileMarketHeaderProps) {
  return (
    <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
      {/* Volume */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1.5,
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: '0.875rem', fontWeight: 500 }}
        >
          {formattedTotalVolume} Vol.
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" title="Copy link" sx={{ p: 0.5 }} onClick={onShareClick}>
            <LinkIcon sx={{ fontSize: '1.125rem' }} />
          </IconButton>
          <IconButton
            size="small"
            title="Bookmark"
            sx={{
              p: 0.5,
              color: isBookmarked ? 'primary.main' : 'inherit',
              transition: 'color 0.2s ease',
              '&:hover': {
                color: isBookmarked ? 'primary.dark' : 'inherit',
              },
            }}
            onClick={onBookmarkClick}
          >
            {isBookmarked ? (
              <BookmarkIcon sx={{ fontSize: '1.125rem' }} />
            ) : (
              <BookmarkBorderIcon sx={{ fontSize: '1.125rem' }} />
            )}
          </IconButton>
        </Box>
      </Box>
      {/* Market Question with Icon */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Avatar
          src={event?.icon || event?.image}
          alt={event.title || 'Market'}
          size={32}
          sx={{ borderRadius: '6px' }}
        />
        <Typography
          variant="h6"
          fontWeight={600}
          sx={{
            fontSize: '1rem',
            wordBreak: 'break-word',
            flex: 1,
            lineHeight: 1.3,
          }}
        >
          {event.title || '-'}
        </Typography>
      </Box>
    </Box>
  );
}
